import { Link } from "react-router-dom";
import {
  ChevronLeft,
  MapPin,
  Building2,
  Clock,
  Coins,
  ShieldCheck,
  FileText,
} from "lucide-react";
import { LATEST } from "@/constants/testIds";
import { ScoreBadge } from "./ScoreBadge";
import { StatusBadge } from "@/components/StatusBadge";
import { EVIDENCE_CLASSES } from "@/components/EvidenceClass";
import {
  MARKET_IMPACT_MAP,
  TIME_SENSITIVITY_MAP,
  TONE_CLASSES,
} from "@/utils/intelligence";
import { deriveEvidenceComposition } from "@/utils/evidence";

/**
 * Opportunity Card — Fursa Design · Phase 1.
 *
 * A concise intelligence dossier (not a news tile). Information priority:
 *   1. Title
 *   2. Maturity / status
 *   3. Country · industry · type
 *   4. Capital class + score + confidence
 *   5. Short executive summary
 *   6. Source · updated · evidence composition (if available)
 *
 * Rules:
 *   • Status conveyed via label + icon + subtle tone (never color-only).
 *   • At most one badge per row — no badge stacks.
 *   • Neutral ink CTA at the footer with chevron.
 */
export const OpportunityCard = ({ opportunity }) => {
  const o = opportunity;
  const meta = opportunity.meta ?? {};

  const impact = meta.marketImpact ? MARKET_IMPACT_MAP[meta.marketImpact] : null;
  const time   = meta.timeSensitivity ? TIME_SENSITIVITY_MAP[meta.timeSensitivity] : null;
  const impactTone = impact ? TONE_CLASSES[impact.tone] : null;
  const timeTone   = time ? TONE_CLASSES[time.tone] : null;
  const evidenceComp = deriveEvidenceComposition(o);

  const publicId = o.id?.replace("opp-", "").replace("feat-", "F-");

  return (
    <article
      data-testid={LATEST.card(o.id)}
      className="group flex h-full flex-col rounded-xl border border-surface-200 bg-white shadow-card transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:border-surface-300 hover:shadow-card-hover"
    >
      <div className="flex flex-1 flex-col p-5">
        {/* 1. Title (primary) */}
        <h3
          data-testid={LATEST.cardTitle(o.id)}
          className="text-[17px] font-semibold leading-[1.35] tracking-tight text-ink-900 arabic-headline line-clamp-2"
        >
          <Link
            to={`/opportunities/${o.id}`}
            data-testid={LATEST.cardReadMore(o.id)}
            className="outline-none hover:text-intel-900"
          >
            {o.title}
          </Link>
        </h3>

        {/* 2. Status + 3. meta (country · industry · type) */}
        <div className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[11px]">
          <StatusBadge
            opportunity={o}
            meta={meta}
            variant="soft"
            size="sm"
            testId={`${LATEST.card(o.id)}-status`}
          />
          <span aria-hidden className="text-surface-300">·</span>
          <span className="inline-flex items-center gap-1 text-ink-700">
            <MapPin className="h-3 w-3" strokeWidth={1.75} aria-hidden />
            {o.country}
          </span>
          <span aria-hidden className="text-surface-300">·</span>
          <span className="inline-flex items-center gap-1 text-ink-700">
            <Building2 className="h-3 w-3" strokeWidth={1.75} aria-hidden />
            {o.industry}
          </span>
          <span aria-hidden className="text-surface-300">·</span>
          <span className="text-ink-500">{o.type}</span>
        </div>

        {/* 4. Capital · Score · Confidence — the executive line */}
        <dl className="mt-4 grid grid-cols-3 gap-3 rounded-lg border border-surface-100 bg-surface-50/60 p-3">
          <MicroStat
            icon={Coins}
            label="رأس المال"
            value={meta.capitalRequired || "غير محدد"}
            ariaLabel={`رأس المال: ${meta.capitalRequired || "غير محدد"}`}
          />
          <div>
            <dt className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest text-ink-500">
              <ShieldCheck className="h-3 w-3" strokeWidth={1.75} aria-hidden />
              النقاط
            </dt>
            <dd className="mt-1.5">
              <ScoreBadge score={o.score} size="sm" />
            </dd>
          </div>
          {typeof meta.confidence === "number" ? (
            <MicroStat
              icon={FileText}
              label="الثقة"
              value={`${meta.confidence}%`}
              valueDir="ltr"
              valueClass="font-mono tabular-nums text-intel-800"
              ariaLabel={`مستوى الثقة: ${meta.confidence}%`}
            />
          ) : (
            <MicroStat icon={Clock} label="الإلحاح" value={time?.label || "—"} valueClass={timeTone?.text} />
          )}
        </dl>

        {/* 5. Executive summary */}
        <p className="mt-4 text-sm leading-relaxed text-ink-700 line-clamp-2 arabic-body">
          {o.summary}
        </p>

        {/* 6. Impact + Timing (evidence composition surfaced when available) */}
        {(impact || time) && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px]">
            {impact && (
              <span className="inline-flex items-center gap-1">
                <span className="text-ink-500">الأثر</span>
                <span className={`font-medium ${impactTone?.text ?? "text-ink-900"}`}>
                  {impact.label}
                </span>
              </span>
            )}
            {time && (
              <span className="inline-flex items-center gap-1">
                <span className="text-ink-500">الإلحاح</span>
                <span className={`font-medium ${timeTone?.text ?? "text-ink-900"}`}>
                  {time.label}
                </span>
              </span>
            )}
          </div>
        )}

        {/* 7. Evidence tally — compact tally row + proportional mini-bar for
              at-a-glance decision support when scanning multiple cards */}
        {evidenceComp && evidenceComp.total > 0 && (
          <div
            data-testid={`card-evidence-${o.id}`}
            className="mt-3"
            aria-label={`تركيبة الأدلّة: ${evidenceComp.total} عناصر`}
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px]">
              <span className="text-ink-500">الأدلّة</span>
              {["verified", "estimated", "expert_assessment"]
                .filter((k) => evidenceComp[k] > 0)
                .map((k) => {
                  const c = EVIDENCE_CLASSES[k];
                  const Icon = c.icon;
                  return (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1 text-ink-700"
                      title={`${c.label} · ${c.hint}`}
                    >
                      <Icon className={`h-3 w-3 ${c.iconTone}`} strokeWidth={1.75} aria-hidden />
                      <span dir="ltr" className="num font-mono font-semibold tabular-nums text-ink-900">
                        {evidenceComp[k]}
                      </span>
                    </span>
                  );
                })}
            </div>
            {/* Proportional mini-bar — a fast visual proxy of evidence
                quality (verified emerald · estimated navy · expert gold ·
                unknown grey). Kept 1px tall to stay quiet in the layout. */}
            <div
              data-testid={`card-evidence-bar-${o.id}`}
              className="mt-1.5 flex h-1 overflow-hidden rounded-full bg-surface-100"
              aria-hidden
            >
              {evidenceComp.verified > 0 && (
                <span className="block h-full bg-fursa-500" style={{ width: `${(evidenceComp.verified / evidenceComp.total) * 100}%` }} />
              )}
              {evidenceComp.estimated > 0 && (
                <span className="block h-full bg-intel-500" style={{ width: `${(evidenceComp.estimated / evidenceComp.total) * 100}%` }} />
              )}
              {evidenceComp.expert_assessment > 0 && (
                <span className="block h-full bg-gold-500" style={{ width: `${(evidenceComp.expert_assessment / evidenceComp.total) * 100}%` }} />
              )}
              {evidenceComp.unknown > 0 && (
                <span className="block h-full bg-surface-300" style={{ width: `${(evidenceComp.unknown / evidenceComp.total) * 100}%` }} />
              )}
            </div>
          </div>
        )}

        {/* Footer: source · updated · CTA */}
        <div className="mt-auto flex items-center justify-between border-t border-surface-100 pt-4">
          <div className="flex items-center gap-2 text-[11px] text-ink-500">
            {o.source_display || o.source ? (
              <span className="truncate max-w-[180px]">{o.source_display || o.source}</span>
            ) : null}
            {publicId && (
              <>
                <span aria-hidden className="text-surface-300">·</span>
                <span dir="ltr" className="font-mono text-[10px] tabular-nums">#{publicId}</span>
              </>
            )}
          </div>
          <Link
            to={`/opportunities/${o.id}`}
            data-testid={`${LATEST.cardReadMore(o.id)}-footer`}
            className="inline-flex items-center gap-1 text-sm font-medium text-ink-700 transition-colors hover:text-intel-900"
            aria-label={`افتح ملف الفرصة: ${o.title}`}
          >
            الملف الكامل
            <ChevronLeft
              className="h-4 w-4 transition-transform group-hover:-translate-x-1"
              strokeWidth={1.75}
              aria-hidden
            />
          </Link>
        </div>
      </div>
    </article>
  );
};

/* ─── Small primitives ─────────────────────────────────────────── */

const MicroStat = ({ icon: Icon, label, value, valueDir, valueClass, ariaLabel }) => (
  <div aria-label={ariaLabel}>
    <dt className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest text-ink-500">
      <Icon className="h-3 w-3" strokeWidth={1.75} aria-hidden />
      {label}
    </dt>
    <dd
      dir={valueDir}
      className={`mt-1.5 truncate text-xs font-semibold text-ink-900 ${valueClass ?? ""}`}
    >
      {value}
    </dd>
  </div>
);
